import React, { useState, useEffect, useMemo } from 'react';
import { Block, Refresh, SearchOff, CheckCircleOutline } from '@mui/icons-material';
import { apiAppFetch } from '../utils/apiFetch';
import '../BlockedScripts.css';

const SEGMENT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'FUTCOM', label: 'MCX' },
  { id: 'FUTSTK', label: 'NSE FUT' },
  { id: 'OPTSTK', label: 'NSE OPT' },
  { id: 'COMEX', label: 'COMEX' },
  { id: 'FOREX', label: 'FOREX' },
  { id: 'GLOBAL', label: 'GLOBAL' },
];

const segmentLabel = (segment) => {
  const map = {
    FUTCOM: 'MCX FUT',
    FUTSTK: 'NSE FUT',
    OPTSTK: 'NSE OPT',
    COMEX: 'COMEX',
    FOREX: 'FOREX',
    GLOBAL: 'GLOBAL',
  };
  return map[segment] || segment || '—';
};

const getBlockScope = (script) => {
  const clientId = script.clientId;
  const parentId = script.parentId;

  if (clientId != null && clientId !== '' && String(clientId) !== '0') {
    return { key: 'client', label: 'Your account' };
  }
  if (parentId != null && parentId !== '' && String(parentId) !== '0') {
    return { key: 'master', label: 'Master rule' };
  }
  return { key: 'global', label: 'Market-wide' };
};

const formatBlockedDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BlockedScripts = () => {
  const [blockedScripts, setBlockedScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [segment, setSegment] = useState('all');

  const fetchBlockedSymbols = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError('');

      const response = await apiAppFetch('getBlockedSymbols', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      const data = await response.json();
      setBlockedScripts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching blocked scripts:', err);
      setError('Could not load blocked scripts. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBlockedSymbols();
  }, []);

  const filteredScripts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return blockedScripts
      .filter((script) => segment === 'all' || script.segment === segment)
      .filter((script) => {
        if (!query) return true;
        const name = String(script.dispname || '').toLowerCase();
        const seg = String(script.segment || '').toLowerCase();
        return name.includes(query) || seg.includes(query);
      })
      .sort((a, b) => String(a.dispname || '').localeCompare(String(b.dispname || '')));
  }, [blockedScripts, search, segment]);

  const segmentCounts = useMemo(() => {
    const counts = { all: blockedScripts.length };
    blockedScripts.forEach((script) => {
      const key = script.segment || 'other';
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [blockedScripts]);

  return (
    <div className="trades-page-wrapper">
      <button
        type="button"
        className="blocked-scripts-refresh"
        onClick={() => fetchBlockedSymbols(true)}
        disabled={loading || refreshing}
        aria-label="Refresh blocked scripts"
      >
        <Refresh />
      </button>

      <div className="trades-page blocked-scripts-page">
        {/* <div className="blocked-scripts-summary">
          <h2>Blocked scripts</h2>
          <p>
            {loading
              ? 'Loading restrictions…'
              : `${blockedScripts.length} script${blockedScripts.length === 1 ? '' : 's'} blocked for trading`}
          </p>
        </div> */}

        {!loading && !error && blockedScripts.length > 0 && (
          <div className="blocked-scripts-toolbar">
            {/* <input
              type="search"
              className="blocked-scripts-search"
              placeholder="Search by symbol or segment…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            /> */}
            <div className="blocked-scripts-filters">
              {SEGMENT_FILTERS.map((item) => {
                const count = segmentCounts[item.id] || 0;
                if (item.id !== 'all' && count === 0) return null;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={segment === item.id ? 'active' : ''}
                    onClick={() => setSegment(item.id)}
                  >
                    {item.label}
                    {count > 0 ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading && (
          <div className="blocked-scripts-state">
            <p>Loading blocked scripts…</p>
          </div>
        )}

        {!loading && error && (
          <div className="blocked-scripts-state error">
            <h3>Something went wrong</h3>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && blockedScripts.length === 0 && (
          <div className="blocked-scripts-state">
            <CheckCircleOutline sx={{ fontSize: 48, color: '#089981' }} />
            <h3>No blocked scripts</h3>
            <p>All symbols available for trading under your account rules.</p>
          </div>
        )}

        {!loading && !error && blockedScripts.length > 0 && filteredScripts.length === 0 && (
          <div className="blocked-scripts-state">
            <SearchOff sx={{ fontSize: 48, color: '#999' }} />
            <h3>No matches</h3>
            <p>Try a different search or clear the segment filter.</p>
          </div>
        )}

        {!loading && !error && filteredScripts.map((script) => {
          const scope = getBlockScope(script);
          const blockedOn = formatBlockedDate(script.blockedDateTime);

          return (
            <article className="blocked-script-card" key={script.id ?? `${script.dispname}-${script.segment}`}>
              <div className="blocked-script-icon" aria-hidden>
                <Block fontSize="small" />
              </div>
              <div className="blocked-script-body">
                <h3 className="blocked-script-name">{script.dispname}</h3>
                <div className="blocked-script-meta">
                  <span className="blocked-script-badge segment">{segmentLabel(script.segment)}</span>
                  <span className={`blocked-script-badge scope-${scope.key}`}>{scope.label}</span>
                  {/* {blockedOn && <span className="blocked-script-date">Since {blockedOn}</span>} */}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default BlockedScripts;
